import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '@/auth/AuthContext';
import { UnifiedIcon } from '@/components/UnifiedIcon';
import { SidebarNavItem } from '@/designSystem/molecules/NavTabs';
import { APP_SHELL_NAV_ITEMS } from '@/layouts/appShellNav';

export interface AppShellSidebarProps {
  mobileOpen: boolean;
  onCloseMobile: () => void;
}

/**
 * Sidebar navigation for the authenticated app shell.
 */
export function AppShellSidebar({ mobileOpen, onCloseMobile }: AppShellSidebarProps) {
  const navigate = useNavigate();
  const { logout, isLoggingOut } = useAuth();

  const handleSignOut = useCallback(async () => {
    await logout();
    navigate('/login', { replace: true });
  }, [logout, navigate]);

  const handleNavClick = useCallback(() => {
    onCloseMobile();
  }, [onCloseMobile]);

  return (
    <>
      {mobileOpen ? (
        <button
          type="button"
          aria-label="Close navigation menu"
          data-testid="agents-app-shell-sidebar-backdrop"
          className="fixed inset-0 z-40 bg-primary/50 md:hidden"
          onClick={onCloseMobile}
        />
      ) : null}

      <aside
        data-testid="agents-app-shell-sidebar"
        className={`fixed inset-y-0 left-0 z-50 flex w-64 flex-col border-r border-default bg-surface transition-transform md:static md:translate-x-0 ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div
          data-testid="agents-app-shell-sidebar-brand"
          className="flex items-center justify-between border-b border-default px-5 py-4"
        >
          <div className="flex items-center gap-2">
            <UnifiedIcon name="Robot" className="text-success" />
            <span className="font-semibold tracking-tight text-primary">Kincode Agents</span>
          </div>
          <button
            type="button"
            aria-label="Close menu"
            data-testid="agents-app-shell-sidebar-close"
            className="rounded-lg p-1 text-muted transition-colors hover:bg-subtle md:hidden"
            onClick={onCloseMobile}
          >
            <UnifiedIcon name="X" size={20} className="h-5 w-5" />
          </button>
        </div>

        <nav data-testid="agents-app-shell-sidebar-nav" className="flex-1 space-y-1 px-3 py-4">
          {APP_SHELL_NAV_ITEMS.map((item) => (
            <SidebarNavItem
              key={item.path}
              label={item.label}
              icon={item.icon}
              to={item.path}
              end={item.path === '/home' || item.path === '/settings'}
              data-testid={item.testId}
              onClick={handleNavClick}
            />
          ))}
        </nav>

        <div data-testid="agents-app-shell-sidebar-footer" className="border-t border-default p-4">
          <button
            type="button"
            data-testid="agents-app-shell-sign-out"
            disabled={isLoggingOut}
            onClick={() => {
              void handleSignOut();
            }}
            className="ds-nav-sidebar-idle flex w-full items-center gap-3 rounded-[var(--kc-radius-sm)] px-3 py-2.5 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-60"
          >
            <UnifiedIcon name="SignOut" size={20} className="h-5 w-5 shrink-0" />
            {isLoggingOut ? 'Signing out…' : 'Sign out'}
          </button>
        </div>
      </aside>
    </>
  );
}
